import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAccessToken } from "../../storage/storage";

// Fallback used until the API responds, or if it ever fails — the footer
// should never go blank. Mirrors the content this component hardcoded
// before it became data-driven.
const FALLBACK = {
  tagline:
    "A smart technology platform that enables hosts and property managers to centralize and control all their cleaning management from one place.",
  phone: "+44 7414 747861",
  email: "hello@housekeeper.host",
  address: "86-90 Paul Street, London EC2A 4NE",
  copyright_text:
    "Copyright © FunSpex Limited, trading as HouseKeeper, 2026. All rights reserved.",
  privacy_policy_url: "https://housekeeper.host/privacy-policy",
  terms_of_service_url: "https://housekeeper.host/terms-of-service",
  support_url: "https://www.housekeeper.host/help",
};

const Footer = () => {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  // null while the fetch is in flight — deliberately NOT pre-filled with
  // FALLBACK, otherwise the fallback text flashes on screen for a moment
  // and then gets swapped for the real data once it arrives, which reads
  // as glitchy any time the two differ (e.g. after editing content in
  // admin). FALLBACK is only used if the fetch genuinely fails.
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/footer-settings/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => setSettings(response.data))
      .catch(() => setSettings(FALLBACK));
  }, [BASE_URL, token]);

  if (!settings) {
    // Same footprint as the real footer so nothing jumps once it loads.
    return <footer className="bg-white border-t border-gray-200 py-4 px-6 mt-auto h-[84px]" />;
  }

  const linkClass = "hover:text-[#586989] hover:underline transition-colors duration-150";
  const Dot = ({ className = "" }) => (
    <span className={`text-gray-300 ${className}`}>•</span>
  );

  return (
    <footer className="bg-white border-t border-gray-200 px-6 py-4 mt-auto">
      <div className="max-w-7xl mx-auto flex flex-col gap-2 text-xs text-gray-500">
        {/* Line 1: tagline (left, 45%) / contact info (right, 55%) —
            fixed columns rather than natural-width blocks with a big
            empty gap between a wrapping tagline and a compact contact
            block. */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center gap-2 md:w-[45%]">
            <img src="/images/main-logo.jpg" alt="HouseKeeper Logo" className="h-8 w-auto flex-shrink-0" />
            <span>{settings.tagline}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:w-[55%] md:justify-end">
            <span>{settings.phone}</span>
            <Dot />
            <span>{settings.email}</span>
            <Dot />
            <span>{settings.address}</span>
          </div>
        </div>

        {/* Line 2: copyright (left) / legal links (right) */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 pt-2 border-t border-gray-100">
          <span>{settings.copyright_text}</span>
          <div className="flex items-center gap-4">
            <a href={settings.privacy_policy_url} target="_blank" rel="noreferrer" className={linkClass}>
              Privacy Policy
            </a>
            <a href={settings.terms_of_service_url} target="_blank" rel="noreferrer" className={linkClass}>
              Terms of Service
            </a>
            <a href={settings.support_url} target="_blank" rel="noreferrer" className={linkClass}>
              Support
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
